import { getCpCancelReasonsApi } from '@apis/cp/orders.js';
import { getRemovalReasonsApi } from '@apis/seller-center/orders.js';
import { Input, RadioGroup, Toast } from '@common-web/index.js';
import { strings } from '@constants/strings';
import { useGetRequest } from '@hooks/index.js';
import { isCP } from '@utils/app-utils.js';
import { formatOptionsForSelect, getErrorString } from '@utils/utils';
import { Flex, Spin } from 'antd';
import { Formik } from 'formik';
import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { ErrorMessage } from '../../../../../../common/web-view';

const CancelModalBody = forwardRef(
  ({ onSubmit = () => {}, data = {}, reasonable_id, description = strings?.please_select_any_reason }, ref) => {
    const { t } = useTranslation();
    const formikRef = useRef(null);
    const getInitialValues = () => ({
      selectedReasonId: '',
      selectedReasonSlug: '',
      customReason: '',
    });

    const { data: reasons, loading } = useGetRequest({
      key: [data?.slug],
      fetch: () => getReasons(),
      noCache: true,
    });

    const getReasons = async (params = '') => {
      try {
        const param = reasonable_id ? `&f[reasonable_id]=${reasonable_id}` : '';
        const res = isCP()
          ? await getCpCancelReasonsApi(`?sort[priority]=asc${param}`)
          : await getRemovalReasonsApi(`?f[reasonable_type]=Disposition&sort[priority]=asc${param}`);
        if (res.success) {
          return res.reasons;
        }
      } catch (e) {
        Toast.error(getErrorString(e));
      }
    };

    const getValidationSchema = () =>
      yup.object().shape({
        selectedReasonId: yup.string().required(t(strings?.please_select_a_reason)),
        customReason: yup.string().when('selectedReasonSlug', {
          is: 'other',
          then: () =>
            yup
              .string()
              .required(t(strings?.field_not_valid_msg))
              .min(10, t(strings?.reason_between_10_50_chars))
              .max(50, t(strings?.reason_between_10_50_chars)),
          otherwise: () => yup.string(),
        }),
      });

    useImperativeHandle(
      ref,
      () => ({
        submitForm: async () => {
          if (formikRef.current) {
            formikRef.current.handleSubmit();
          }
        },
        getFormValues: () => {
          return formikRef.current.values;
        },
        resetForm: () => {
          if (formikRef.current) {
            formikRef.current.resetForm();
          }
        },
      }),
      []
    );

    if (loading) {
      return <Spin style={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center' }} />;
    }

    return (
      <Formik
        initialValues={getInitialValues()}
        validationSchema={getValidationSchema()}
        onSubmit={onSubmit}
        innerRef={formikRef}
        validateOnChange={true}
      >
        {(formikProps) => {
          const { values, setFieldValue, handleSubmit, setFieldTouched, errors } = formikProps;
          return (
            <form onSubmit={handleSubmit}>
              <Flex vertical gap={16}>
                <div>
                  <p style={{ marginBottom: 11, marginTop: 1 }}>{t(description)}</p>
                  <RadioGroup
                    options={formatOptionsForSelect({ list: reasons })}
                    value={values?.selectedReasonId}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 16,
                    }}
                    onChange={async (e) => {
                      const slug = reasons?.find((reason) => reason?.id === e.target.value)?.slug;
                      await setFieldValue('selectedReasonId', e.target.value);
                      await setFieldValue('selectedReasonSlug', slug);
                      if (slug === 'other') {
                        setFieldTouched('customReason', false);
                      }
                    }}
                  />
                  <ErrorMessage errorMsg={errors?.selectedReasonId} />
                </div>
                {values.selectedReasonSlug === 'other' && (
                  <>
                    <Input
                      label={t(strings?.additional_information)}
                      required={true}
                      placeholder={t(strings?.please_specify_your_reason)}
                      name="customReason"
                      autoFocus={true}
                      value={values.customReason}
                      onChange={(e) => {
                        setFieldTouched('customReason', true);
                        setFieldValue('customReason', e.target.value);
                      }}
                      onBlur={(e) => {
                        setFieldTouched('customReason', true);
                        setFieldValue('customReason', e.target.value);
                      }}
                      rows={4}
                      errorMsg={formikProps?.touched?.customReason && formikProps?.errors?.customReason}
                    />
                  </>
                )}
              </Flex>
            </form>
          );
        }}
      </Formik>
    );
  }
);

export default CancelModalBody;
